import { h, ref, provide, inject, onMounted, onBeforeUnmount, watch, computed } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

const COLLAPSE_KEY = Symbol("bv-collapse");

function normalizeClasses(attrs) {
  const cls = attrs.class;
  if (!cls) return [];
  if (Array.isArray(cls)) return cls.flat();
  if (typeof cls === "string") return cls.split(/\s+/).filter(Boolean);
  if (typeof cls === "object") {
    return Object.keys(cls).filter(key => cls[key]);
  }
  return [];
}

function baseProps(attrs, extraClass = []) {
  const { class: _c, style, ...rest } = attrs;
  return {
    class: [...normalizeClasses(attrs), ...extraClass],
    style,
    ...rest
  };
}

const BContainer = {
  name: "BContainer",
  props: { fluid: Boolean },
  setup(props, { slots, attrs }) {
    return () =>
      h(
        "div",
        baseProps(attrs, [props.fluid ? "container-fluid" : "container"]),
        slots.default?.()
      );
  }
};

const BRow = {
  name: "BRow",
  setup(_, { slots, attrs }) {
    return () => h("div", baseProps(attrs, ["row"]), slots.default?.());
  }
};

const BCol = {
  name: "BCol",
  props: {
    cols: [String, Number],
    sm: [String, Number, Boolean],
    md: [String, Number, Boolean],
    lg: [String, Number, Boolean],
    xl: [String, Number, Boolean]
  },
  setup(props, { slots, attrs }) {
    return () => {
      const classes = [];
      if (props.cols) classes.push(`col-${props.cols}`);
      ["sm", "md", "lg", "xl"].forEach(bp => {
        const val = props[bp];
        if (val === true) classes.push(`col-${bp}`);
        else if (val) classes.push(`col-${bp}-${val}`);
      });
      if (!classes.length) classes.push("col");
      return h("div", baseProps(attrs, classes), slots.default?.());
    };
  }
};

const BCard = {
  name: "BCard",
  props: {
    noBody: Boolean,
    tag: { type: String, default: "div" }
  },
  setup(props, { slots, attrs }) {
    return () => {
      const children = [];
      if (slots.header) {
        children.push(h("div", { class: "card-header" }, slots.header()));
      }
      if (props.noBody) {
        if (slots.default) children.push(...slots.default());
      } else if (slots.default) {
        children.push(h("div", { class: "card-body" }, slots.default()));
      }
      if (slots.footer) {
        children.push(h("div", { class: "card-footer" }, slots.footer()));
      }
      return h(props.tag, baseProps(attrs, ["card"]), children);
    };
  }
};

const BCardHeader = {
  name: "BCardHeader",
  props: {
    headerTag: { type: String, default: "div" },
    tag: String
  },
  setup(props, { slots, attrs }) {
    return () =>
      h(
        props.tag || props.headerTag || "div",
        baseProps(attrs, ["card-header"]),
        slots.default?.()
      );
  }
};

const BCardBody = {
  name: "BCardBody",
  setup(_, { slots, attrs }) {
    return () => h("div", baseProps(attrs, ["card-body"]), slots.default?.());
  }
};

const BCardText = {
  name: "BCardText",
  setup(_, { slots, attrs }) {
    return () => h("p", baseProps(attrs, ["card-text"]), slots.default?.());
  }
};

const BCardImg = {
  name: "BCardImg",
  props: {
    src: String,
    alt: { type: String, default: "" },
    top: Boolean,
    bottom: Boolean
  },
  setup(props, { attrs }) {
    return () =>
      h("img", {
        ...baseProps(attrs, [
          "card-img",
          props.top ? "card-img-top" : "",
          props.bottom ? "card-img-bottom" : ""
        ]),
        src: props.src,
        alt: props.alt
      });
  }
};

const BCardGroup = {
  name: "BCardGroup",
  props: { deck: Boolean, columns: Boolean },
  setup(props, { slots, attrs }) {
    return () =>
      h(
        "div",
        baseProps(attrs, [
          props.columns ? "card-columns" : props.deck ? "card-deck" : "card-group"
        ]),
        slots.default?.()
      );
  }
};

const BButton = {
  name: "BButton",
  props: {
    variant: { type: String, default: "secondary" },
    type: { type: String, default: "button" },
    block: Boolean,
    disabled: Boolean,
    size: String,
    href: String,
    tag: String
  },
  emits: ["click"],
  setup(props, { slots, attrs, emit }) {
    return () => {
      const tag = props.tag || (props.href ? "a" : "button");
      const classes = [
        "btn",
        `btn-${props.variant}`,
        props.block ? "btn-block" : "",
        props.size ? `btn-${props.size}` : ""
      ];
      return h(
        tag,
        {
          ...baseProps(attrs, classes),
          type: tag === "button" ? props.type : undefined,
          href: props.href,
          disabled: props.disabled,
          onClick: e => emit("click", e)
        },
        slots.default?.()
      );
    };
  }
};

const collapseRegistry = new Map();

function toggleCollapse(id) {
  const entry = collapseRegistry.get(id);
  if (!entry) return;
  entry.visible.value = !entry.visible.value;
  if (entry.accordion) {
    collapseRegistry.forEach((other, otherId) => {
      if (otherId !== id && other.accordion === entry.accordion) {
        other.visible.value = false;
      }
    });
  }
}

const BCollapse = {
  name: "BCollapse",
  props: {
    id: String,
    modelValue: { type: Boolean, default: undefined },
    visible: { type: Boolean, default: undefined },
    accordion: String,
    tag: { type: String, default: "div" },
    appear: Boolean
  },
  emits: ["update:modelValue", "input", "show", "shown", "hide", "hidden"],
  setup(props, { slots, attrs, emit }) {
    const visible = ref(
      props.modelValue ?? props.visible ?? false
    );

    watch(
      () => props.modelValue,
      val => {
        if (val !== undefined) visible.value = val;
      }
    );
    watch(
      () => props.visible,
      val => {
        if (val !== undefined) visible.value = val;
      }
    );
    watch(visible, val => {
      emit("update:modelValue", val);
      emit("input", val);
      emit(val ? "show" : "hide");
      emit(val ? "shown" : "hidden");
    });

    onMounted(() => {
      if (props.id) {
        collapseRegistry.set(props.id, {
          visible,
          accordion: props.accordion
        });
      }
      // Bootstrap "show" class in markup means start open
      const classes = normalizeClasses(attrs);
      if (classes.includes("show") || classes.includes("showContent")) {
        visible.value = true;
      }
    });
    onBeforeUnmount(() => {
      if (props.id) collapseRegistry.delete(props.id);
    });

    provide(COLLAPSE_KEY, { visible, toggle: () => (visible.value = !visible.value) });

    return () =>
      h(
        props.tag,
        {
          ...baseProps(attrs, ["collapse", visible.value ? "show" : ""]),
          id: props.id,
          style: {
            ...(attrs.style || {}),
            display: visible.value ? undefined : "none"
          }
        },
        slots.default?.()
      );
  }
};

const BForm = {
  name: "BForm",
  emits: ["submit"],
  setup(_, { slots, attrs, emit }) {
    return () =>
      h(
        "form",
        {
          ...baseProps(attrs),
          onSubmit: e => {
            e.preventDefault();
            emit("submit", e);
          }
        },
        slots.default?.()
      );
  }
};

const BFormGroup = {
  name: "BFormGroup",
  props: {
    label: String,
    labelFor: String,
    description: String
  },
  setup(props, { slots, attrs }) {
    return () =>
      h("div", baseProps(attrs, ["form-group"]), [
        props.label
          ? h("label", { for: props.labelFor }, props.label)
          : null,
        slots.default?.(),
        props.description
          ? h("small", { class: "form-text text-muted" }, props.description)
          : null
      ]);
  }
};

const BFormInput = {
  name: "BFormInput",
  props: {
    modelValue: [String, Number],
    value: [String, Number],
    type: { type: String, default: "text" },
    placeholder: String,
    disabled: Boolean,
    required: Boolean,
    size: String,
    state: { type: [Boolean, null], default: null }
  },
  emits: ["update:modelValue", "input", "change"],
  setup(props, { attrs, emit }) {
    return () => {
      const current = props.modelValue !== undefined ? props.modelValue : props.value;
      return h("input", {
        ...baseProps(attrs, [
          "form-control",
          props.size ? `form-control-${props.size}` : "",
          props.state === false ? "is-invalid" : props.state === true ? "is-valid" : ""
        ]),
        type: props.type,
        value: current ?? "",
        placeholder: props.placeholder,
        disabled: props.disabled,
        required: props.required,
        onInput: e => {
          emit("update:modelValue", e.target.value);
          emit("input", e.target.value);
        },
        onChange: e => emit("change", e.target.value)
      });
    };
  }
};

const BFormSelect = {
  name: "BFormSelect",
  props: {
    modelValue: [String, Number, Array],
    value: [String, Number, Array],
    options: { type: Array, default: () => [] },
    disabled: Boolean
  },
  emits: ["update:modelValue", "input", "change"],
  setup(props, { slots, attrs, emit }) {
    return () => {
      const current = props.modelValue !== undefined ? props.modelValue : props.value;
      const optionNodes =
        slots.default?.() ||
        props.options.map(opt => {
          if (opt && typeof opt === "object") {
            return h("option", { value: opt.value }, opt.text ?? opt.label ?? opt.value);
          }
          return h("option", { value: opt }, String(opt));
        });
      return h(
        "select",
        {
          ...baseProps(attrs, ["form-control", "custom-select"]),
          value: current,
          disabled: props.disabled,
          onChange: e => {
            emit("update:modelValue", e.target.value);
            emit("input", e.target.value);
            emit("change", e.target.value);
          }
        },
        optionNodes
      );
    };
  }
};

const BFormDatepicker = {
  name: "BFormDatepicker",
  props: {
    modelValue: [String, Date, null],
    value: [String, Date, null],
    placeholder: String,
    disabled: Boolean
  },
  emits: ["update:modelValue", "input"],
  setup(props, { attrs, emit }) {
    return () => {
      const current = props.modelValue !== undefined ? props.modelValue : props.value;
      return h(VueDatePicker, {
        ...baseProps(attrs, ["b-form-datepicker"]),
        modelValue: current,
        disabled: props.disabled,
        placeholder: props.placeholder,
        "onUpdate:modelValue": val => {
          emit("update:modelValue", val);
          emit("input", val);
        }
      });
    };
  }
};

const BAlert = {
  name: "BAlert",
  props: {
    show: { type: [Boolean, Number, String], default: false },
    variant: { type: String, default: "info" },
    dismissible: Boolean
  },
  emits: ["dismissed", "input"],
  setup(props, { slots, attrs, emit }) {
    const visible = ref(!!props.show);
    watch(
      () => props.show,
      val => {
        visible.value = !!val;
      }
    );
    return () => {
      if (!visible.value) return null;
      return h("div", baseProps(attrs, ["alert", `alert-${props.variant}`]), [
        slots.default?.(),
        props.dismissible
          ? h(
              "button",
              {
                type: "button",
                class: "close",
                onClick: () => {
                  visible.value = false;
                  emit("dismissed");
                  emit("input", false);
                }
              },
              [h("span", { "aria-hidden": "true" }, "×")]
            )
          : null
      ]);
    };
  }
};

const BModal = {
  name: "BModal",
  props: {
    modelValue: Boolean,
    visible: Boolean,
    title: String,
    id: String,
    hideFooter: Boolean,
    size: String,
    centered: Boolean
  },
  emits: ["update:modelValue", "change", "hide", "hidden", "show", "shown", "ok", "cancel"],
  setup(props, { slots, attrs, emit }) {
    const open = computed(() => props.modelValue || props.visible);
    const close = () => {
      emit("update:modelValue", false);
      emit("change", false);
      emit("hide");
      emit("hidden");
      emit("cancel");
    };
    return () => {
      if (!open.value) return null;
      return h("div", { class: "modal fade show d-block", id: props.id, style: { background: "rgba(0,0,0,.5)" } }, [
        h(
          "div",
          {
            class: [
              "modal-dialog",
              props.size ? `modal-${props.size}` : "",
              props.centered ? "modal-dialog-centered" : ""
            ]
          },
          [
            h("div", baseProps(attrs, ["modal-content"]), [
              h("div", { class: "modal-header" }, [
                h("h5", { class: "modal-title" }, props.title || slots["modal-title"]?.() || slots.title?.()),
                h("button", { type: "button", class: "close", onClick: close }, [
                  h("span", "×")
                ])
              ]),
              h("div", { class: "modal-body" }, slots.default?.()),
              props.hideFooter
                ? null
                : h("div", { class: "modal-footer" }, [
                    slots["modal-footer"]?.() ||
                      slots.footer?.() || [
                        h(
                          "button",
                          {
                            type: "button",
                            class: "btn btn-secondary",
                            onClick: close
                          },
                          "Close"
                        ),
                        h(
                          "button",
                          {
                            type: "button",
                            class: "btn btn-primary",
                            onClick: () => {
                              emit("ok");
                              close();
                            }
                          },
                          "OK"
                        )
                      ]
                  ])
            ])
          ]
        )
      ]);
    };
  }
};

const BEmbed = {
  name: "BEmbed",
  props: {
    type: { type: String, default: "iframe" },
    src: String,
    aspect: { type: String, default: "16by9" }
  },
  setup(props, { attrs }) {
    return () =>
      h("div", baseProps(attrs, ["embed-responsive", `embed-responsive-${props.aspect}`]), [
        h(props.type, {
          class: "embed-responsive-item",
          src: props.src,
          allowfullscreen: true
        })
      ]);
  }
};

function VBToggle(el, binding) {
  const targetId =
    typeof binding.value === "string"
      ? binding.value
      : binding.arg ||
        (binding.value && binding.value.id) ||
        Object.keys(binding.modifiers || {})[0];

  el.__bvToggleHandler = e => {
    e.preventDefault();
    if (targetId) toggleCollapse(targetId);
  };
  el.addEventListener("click", el.__bvToggleHandler);
  el.setAttribute("aria-controls", targetId || "");
  el.style.cursor = el.style.cursor || "pointer";
}

function VBToggleUnbind(el) {
  if (el.__bvToggleHandler) {
    el.removeEventListener("click", el.__bvToggleHandler);
    delete el.__bvToggleHandler;
  }
}

const VBTooltip = {
  mounted(el, binding) {
    const title =
      typeof binding.value === "string"
        ? binding.value
        : binding.value?.title || el.getAttribute("title") || "";
    el.setAttribute("title", title);
    el.setAttribute("data-toggle", "tooltip");
  },
  updated(el, binding) {
    const title =
      typeof binding.value === "string"
        ? binding.value
        : binding.value?.title || el.getAttribute("title") || "";
    el.setAttribute("title", title);
  }
};

export default {
  install(app) {
    const components = {
      BContainer,
      BRow,
      BCol,
      BCard,
      BCardHeader,
      BCardBody,
      BCardText,
      BCardImg,
      BCardGroup,
      BButton,
      BCollapse,
      BForm,
      BFormGroup,
      BFormInput,
      BFormSelect,
      BFormDatepicker,
      BAlert,
      BModal,
      BEmbed
    };
    Object.entries(components).forEach(([name, component]) => {
      app.component(name, component);
      app.component(
        name
          .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
          .toLowerCase(),
        component
      );
    });
    // Explicit kebab aliases matching templates
    app.component("b-container", BContainer);
    app.component("b-row", BRow);
    app.component("b-col", BCol);
    app.component("b-card", BCard);
    app.component("b-card-header", BCardHeader);
    app.component("b-card-body", BCardBody);
    app.component("b-card-text", BCardText);
    app.component("b-card-img", BCardImg);
    app.component("b-card-group", BCardGroup);
    app.component("b-button", BButton);
    app.component("b-collapse", BCollapse);
    app.component("b-form", BForm);
    app.component("b-form-group", BFormGroup);
    app.component("b-form-input", BFormInput);
    app.component("b-form-select", BFormSelect);
    app.component("b-form-datepicker", BFormDatepicker);
    app.component("b-alert", BAlert);
    app.component("b-modal", BModal);
    app.component("b-embed", BEmbed);

    app.directive("b-toggle", {
      mounted: VBToggle,
      updated(el, binding) {
        VBToggleUnbind(el);
        VBToggle(el, binding);
      },
      unmounted: VBToggleUnbind
    });
    app.directive("b-tooltip", VBTooltip);
  }
};
